import { ExternalLink } from "@/components/ui/external-link";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { Icon } from "@/components/ui/icon"
import {
  type EntityAttachment,
  isImageAttachment,
  isPdfAttachment,
} from "@/types/entity-attachment";

type EntityAttachmentsPanelProps = {
  attachments?: EntityAttachment[];
  title?: string;
};

export function EntityAttachmentsPanel({
  attachments = [],
  title = "Arquivos",
}: EntityAttachmentsPanelProps) {
  if (!attachments.length) return null;

  return (
    <section className="bg-card-surface rounded-2xl border border-card-border p-6 shadow-sm flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Icon name="attach_file" />
        </span>
        <div>
          <h3 className="text-lg font-bold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground">
            {attachments.length} arquivo{attachments.length === 1 ? "" : "s"}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {attachments.map((attachment) => {
          if (isImageAttachment(attachment.mimeType)) {
            return (
              <a
                key={attachment.id}
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group overflow-hidden rounded-xl border border-border bg-muted/20 hover:border-primary/40 transition-colors"
              >
                <OptimizedImage
                  src={attachment.url}
                  alt="Imagem anexada"
                  className="block w-full h-auto object-contain transition-transform group-hover:scale-[1.01]"
                />
              </a>
            );
          }

          if (isPdfAttachment(attachment.mimeType)) {
            return (
              <div
                key={attachment.id}
                className="overflow-hidden rounded-xl border border-border bg-muted/20"
              >
                <iframe
                  src={attachment.url}
                  title="PDF anexado"
                  className="block w-full min-h-[32rem] border-0"
                />
                <div className="border-t border-border px-4 py-2.5 bg-muted/10">
                  <ExternalLink
                    href={attachment.url}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                  >
                    Abrir PDF em nova aba
                    <Icon name="open_in_new" />
                  </ExternalLink>
                </div>
              </div>
            );
          }

          return (
            <ExternalLink
              key={attachment.id}
              href={attachment.url}
              className="flex items-center justify-center gap-2 rounded-xl border border-border bg-muted/20 px-4 py-6 text-sm font-semibold text-primary hover:border-primary/40 hover:bg-primary/5 transition-colors"
            >
              Abrir arquivo
              <Icon name="open_in_new" />
            </ExternalLink>
          );
        })}
      </div>
    </section>
  );
}
